import { DateTime } from 'luxon'
import { Module, Mutation, Action, Model, ApiClient, store } from './helpers'
import { Locations } from './Locations'
import { IPhotoData } from './Templates'

export enum MessageType {
  text = 'DigitalSignage::Message::Text',
  urgent = 'DigitalSignage::Message::Urgent',
  url = 'DigitalSignage::Message::Url',
  photo = 'DigitalSignage::Message::Photo',
}

export interface MessageModel {
  company_id: number
  content: string
  end_time: string|null
  id: number
  start_time: string|null
  status: string
  title: string
  type: MessageType
  location_ids: number[]
  mediaType: 'video'|'image'
  photos?: IPhotoData[]
  photo_resources?: IPhotoData[]
  parsedPhotoData?: IPhotoData
}

const tickerMessageTypes: MessageType[] = [ MessageType.text, MessageType.urgent ]

@Module
class MessagesModule extends Model<MessageModel> {
  activeMessages: MessageModel[] = []
  activeTickerMessages: MessageModel[] = []
  activeEmergencyMessages: MessageModel[] = []
  slideshowMessages: MessageModel[] = []
  videoMessages: MessageModel[] = []
  emergency = false

  @Action async GET_MESSAGES() {
    try {
      const { data } = await ApiClient.get('/api/v1/digital_signage/messages?message_location_ids=true&photos=true')
      this.STORE_ENTITIES(data.messages)
      this.CALCULATE_ACTIVE_MESSAGES()
    } catch (error) {
      // network error occured
    }
  }

  @Action async CREATE_MESSAGE(message: Partial<MessageModel>) {
    const { data } = await ApiClient.post('/api/v1/digital_signage/messages', { message })
    this.STORE_ENTITY(data.message)
    this.CALCULATE_ACTIVE_MESSAGES()
  }

  @Action RECALCULATE_MESSAGES_FOR_CURRENT_LOCATION() {
    this.CALCULATE_ACTIVE_MESSAGES()
  }

  @Action RESET() {
    this.RESET_ALL()
  }

  @Mutation RESET_ALL() {
    this.activeMessages = []
    this.activeTickerMessages = []
    this.activeEmergencyMessages = []
    this.slideshowMessages = []
    this.videoMessages = []
    this.emergency = false
  }

  @Mutation CALCULATE_ACTIVE_MESSAGES() {
    const now = DateTime.local()
    const activeLocationId = Locations.activeId
    this.emergency = false
    this.activeEmergencyMessages = []
    this.activeMessages = []
    this.slideshowMessages = []
    // console.log('we are at location', activeLocationId)
    this.activeMessages = this.entities.filter((entity) => {
      if (entity.status !== 'active') {
        // console.log('message is inactive', entity)
        return false
      }

      if (entity.start_time || entity.end_time) {
        const startDate = DateTime.fromISO(entity.start_time)
        if (now.diff(startDate).valueOf() < 0) { return false }
        const endDate = DateTime.fromISO(entity.end_time)
        if (now.diff(endDate).valueOf() > 0) { return false }
      }

      if (activeLocationId) {
        if (!entity.location_ids.includes(activeLocationId)) {
          // console.log('not available at this location')
          return false
        }
      }

      switch (entity.type) {
        case MessageType.text:
          return true
        case MessageType.urgent:
          this.emergency = true
          this.activeEmergencyMessages.push(entity)
          return false
        case MessageType.url:
          if (entity.content && entity.content.indexOf('youtube') >= 0) {
            entity.mediaType = 'video'
          } else {
            entity.mediaType = 'image' // fallback
          }
          if (entity.content && entity.content.trim().length > 0) { // discard empty message contents
            this.slideshowMessages.push(entity)
          }
          return false
        case MessageType.photo:
          entity.mediaType = 'image'
          const foundPhotoData = entity.photos.find((p) => !p._destroy)
          if (!foundPhotoData) { return false }
          entity.content = foundPhotoData.original // set photo for easy retrieval
          this.slideshowMessages.push(entity)
          return false
      }
    })
  }
}

export const Messages = new MessagesModule({ store, name: 'Messages' })
